import * as React from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { IMap, IDriverSearchObject, IVehicleObj, IVehicleFilters } from '../../types'
import { Column, usePagination, useSortBy, useTable } from 'react-table'
import ReactTablePagination from '../ui/ReactTablePagination'
import ContextMenu from '../ui/ContextMenu'
import { Link } from 'react-router-dom'
import EditIcon from '../icons/ui/Edit'
import Activate from '../icons/vehicle/Activate'
import Deactivate from '../icons/vehicle/Deactivate'
import Exclude from '../icons/vehicle/Exclude'
import classNames from 'classnames'
import moment from 'moment'
import { MoonLoader } from 'react-spinners'
import { NotificationProvider } from '../ui/notification-provider'

export interface ITableVehiclesProps {
  vehicles: IVehicleObj[]
  totalCount: number
  selectedVehicles: IMap<string>
  operators: IMap<IDriverSearchObject>
  organizationId: string
  isFetch: boolean
  error: string | null
  isDeactivatingVehicle: boolean
  list: string
  filters: IVehicleFilters
  fetchAllVehicles: (params: IVehicleFilters) => void
  updateVehicleFilters: (param: IVehicleFilters) => Promise<void>
  activateVehicleAndFetchAllVehicles: (param: IVehicleFilters, id: string) => Promise<void>
  deactivateVehicleAndFetchAllVehicles: (param: IVehicleFilters, id: string) => Promise<void>
  removeVehicleAndFetchAllVehicles: (param: IVehicleFilters, id: string) => Promise<void>
  unassignDriver: (id: string, operatorId: string, params: IVehicleFilters) => Promise<any>
}

export interface ITableDriversState {
  isArchived: boolean
  search: string
  pages: number
}

interface IProps<T extends object> {
  columns: Array<Column<T>>
  filters: IVehicleFilters
  data: T[]
  loading: boolean
  pageCount: number
  fetchData: (params: { pageIndex: number; pageSize: number }) => void
  updateFilters: (params: IVehicleFilters) => Promise<any>
  pageSize: number
  setPageSize: (size: number) => any
  pageIndex: number
  setPageIndex: (size: number) => any
}

const PAGE_SIZE = 25

function Table<T extends object>({
  columns,
  data,
  fetchData,
  updateFilters,
  filters,
  loading,
  pageCount: controlledPageCount,
  pageSize,
  setPageSize,
  pageIndex,
  setPageIndex
}: IProps<T>) {
  const { getTableProps, getTableBodyProps, headerGroups, rows, page, prepareRow, state } = useTable<T>(
    {
      columns,
      data,
      initialState: {
        pageIndex: 0,
        pageSize: PAGE_SIZE
      },
      manualSortBy: true,
      manualPagination: true,
      pageCount: controlledPageCount
    },
    useSortBy,
    usePagination
  )

  const { sortBy } = state
  const mounted = useRef<boolean>()
  const [canPreviousPage, setCanPreviousPage] = React.useState(false)
  const [canNextPage, setCanNextPage] = React.useState(false)

  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true
    } else {
      fetchData({ pageIndex, pageSize })
    }
  }, [pageIndex, pageSize])

  useEffect(() => {
    let sort = ''
    let orderBy = ''
    sortBy.map(s => {
      s.desc ? (sort = 'DESC') : (sort = 'ASC')
      orderBy = s.id
    })
    if (sort && orderBy) {
      updateFilters({ orderBy, sort })
    }
  }, [sortBy])

  useEffect(() => {
    if (pageIndex > 0) {
      setCanPreviousPage(true)
    } else {
      setCanPreviousPage(false)
    }

    if (pageIndex === controlledPageCount - 1) {
      setCanNextPage(false)
    } else {
      setCanNextPage(true)
    }
  }, [pageIndex, pageSize, controlledPageCount])

  useEffect(() => {
    setPageIndex(0)
  }, [pageSize])

  const nextPage = () => {
    setPageIndex(pageIndex + 1)
  }

  const previousPage = () => {
    setPageIndex(pageIndex - 1)
  }

  return (
    <React.Fragment>
      {loading ? (
        <div className='flex flex-column justify-center items-center' style={{ width: '90%', height: '40vh' }}>
          <MoonLoader sizeUnit={'px'} size={40} color={'#405264'} loading={true} />
          <span className='pt1'>Loading...</span>
        </div>
      ) : (
        <table {...getTableProps()}>
          <thead>
            {headerGroups.map(headerGroup => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map(column => (
                  <th
                    className={classNames('', {
                      sortable: column.canSort,
                      sortedAsc: column.isSortedDesc === false,
                      sortedDesc: column.isSortedDesc === true
                    })}
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                  >
                    {column.render('Header')}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {loading ? (
              <tr>
                <td>Loading...</td>
              </tr>
            ) : (
              rows.map(
                row =>
                  +prepareRow(row) || (
                    <tr {...row.getRowProps()}>
                      {row.cells.map(cell => {
                        return (
                          <td {...cell.getCellProps()} key={`vehiclesTableColumn-${cell.row.index}-${cell.column.id}`}>
                            {cell.render('Cell')}
                          </td>
                        )
                      })}
                    </tr>
                  )
              )
            )}
          </tbody>
        </table>
      )}
      <ReactTablePagination
        page={pageIndex}
        pages={controlledPageCount}
        pageSize={pageSize}
        canNext={canNextPage}
        canPrevious={canPreviousPage}
        nextPage={nextPage}
        previousPage={previousPage}
        setPageSize={setPageSize}
        rowCount={rows.length}
      />
    </React.Fragment>
  )
}

export default function VehiclesTable(props: ITableVehiclesProps) {
  const notification: any = React.useContext(NotificationProvider)
  const [loading, setLoading] = useState(false)
  const [pageSize, setPageSize] = useState(PAGE_SIZE)
  const [pageCount, setPageCount] = React.useState(0)
  const [pageIndex, setPageIndex] = React.useState(0)

  const columns = useMemo(
    () => [
      {
        id: 'vehicles.type',
        Header: 'Car Type',
        accessor: (vehicle: IVehicleObj) => {
          return <Link to={`/vehicles/view/${vehicle.id}/details`}> {vehicle.type}</Link>
        },
        width: 200
      },
      {
        id: 'vehicles.year',
        Header: 'Year',
        accessor: (vehicle: IVehicleObj) => {
          return <Link to={`/vehicles/view/${vehicle.id}/details`}> {vehicle.year}</Link>
        },
        className: 'flex items-center'
      },
      {
        id: 'vehicles_models.make',
        Header: 'Make',
        accessor: (vehicle: IVehicleObj) => {
          return <Link to={`/vehicles/view/${vehicle.id}/details`}> {vehicle.make}</Link>
        },
        className: 'flex items-center'
      },
      {
        id: 'vehicles.model',
        Header: 'Model',
        accessor: (vehicle: IVehicleObj) => {
          return <Link to={`/vehicles/view/${vehicle.id}/details`}> {vehicle.model}</Link>
        },
        className: 'overflow--visible'
      },
      {
        id: 'vehicles.licensePlate',
        Header: 'License Plate',
        accessor: (vehicle: IVehicleObj) => {
          return <Link to={`/vehicles/view/${vehicle.id}/details`}> {vehicle.licensePlate}</Link>
        },
        className: 'flex items-center'
      },
      {
        id: 'vehicleProviders.provider',
        Header: 'Provider',
        accessor: (vehicle: IVehicleObj) => {
          const provider = vehicle.organization && vehicle.organization.name ? vehicle.organization.name : ''
          return <div>{provider}</div>
        },
        className: 'flex items-center'
      },
      {
        id: 'vehicles.isInRide',
        Header: 'Status',
        disableSortBy: true,
        accessor: (vehicle: IVehicleObj) => {
          const status = vehicle.isInRide ? 'In Ride' : 'Not in Ride'
          return <div>{status}</div>
        },
        className: 'flex items-center'
      },
      {
        id: 'vehicles.operator',
        Header: 'Driver',
        accessor: (vehicle: IVehicleObj) => {
          if (vehicle.name) {
            return <div>{vehicle.name}</div>
          }
          return <div />
        },
        className: 'flex items-center'
      },
      {
        id: 'vehicles.checkoutBy',
        Header: 'Previous',
        accessor: (vehicle: IVehicleObj) => {
          if (vehicle.checkoutBy) {
            return <div>{vehicle.checkoutBy}</div>
          }
          return <div />
        },
        className: 'flex items-center'
      },
      {
        id: 'vehicles.checkoutDate',
        Header: 'Checkout Date',
        accessor: (vehicle: IVehicleObj) => {
          if (vehicle.checkoutDate) {
            const lastCheckOutDate = moment(vehicle.checkoutDate).format('MMMM D YYYY, HH:mm')
            return <div>{lastCheckOutDate}</div>
          }
          return <div />
        },
        className: 'flex items-center'
      },
      {
        id: 'vehicles.isDedicated',
        Header: 'Dedicated',
        disableSortBy: true,
        accessor: (vehicle: IVehicleObj) => {
          const isDedicated = vehicle.isDedicated ? 'Yes' : 'No'
          return <div>{isDedicated}</div>
        },
        className: 'flex items-center'
      },
      {
        Header: '',
        id: 'context-menu',
        Cell: (data: any) => {
          return (
            <CellContextMenu
              operatorId={data.row.original.operatorId}
              vehicle={data.row.original}
              deactiveVehicle={deactiveVehicle}
              activeVehicle={activeVehicle}
              removeVehicle={removeVehicle}
              unassignDriver={unassignDriver}
              filters={props.filters}
            />
          )
        }
      }
    ],
    [props.operators]
  )
  const CellContextMenu = (props: {
    vehicle: IVehicleObj
    operatorId: string
    deactiveVehicle: (item: IVehicleFilters, id: string) => () => void
    activeVehicle: (item: IVehicleFilters, id: string) => () => void
    removeVehicle: (item: IVehicleFilters, id: string) => () => void
    unassignDriver: (id: string, operatorId: string, params: IVehicleFilters) => () => void
    filters: IVehicleFilters
  }) => {
    const { operatorId, isActive, id } = props.vehicle

    return (
      <div className='flex items-center'>
        <ContextMenu style={{ zDepth: -1 }}>
          {!isActive ? (
            <div className='MenuItem flex items-center cursor--pointer p1'>
              <div onClick={props.activeVehicle(props.filters, id)}>
                <Activate className='icon--small' />
                <span className='font-dark-grey ml1 h6'>Activate</span>
              </div>
            </div>
          ) : (
            <div className='MenuItem flex items-center cursor--pointer p1'>
              <div className='col-12 flex cursor--pointer' onClick={props.deactiveVehicle(props.filters, id)}>
                <Deactivate className='icon--small' />
                <span className='font-dark-grey ml1 h6'>Deactivate</span>
              </div>
            </div>
          )}
          {operatorId ? (
            <div className='MenuItem flex items-center cursor--pointer p1'>
              <div
                className='col-12 flex cursor--pointer'
                onClick={props.unassignDriver(id, operatorId, props.filters)}
              >
                <Deactivate className='icon--small' />
                <span className='font-dark-grey ml1 h6'>Unassign Driver</span>
              </div>
            </div>
          ) : (
            <div className='MenuItem flex items-center cursor--pointer p1'>
              <div className='col-12 flex cursor--pointer'>
                <Link to={`/vehicles/${props.vehicle.id}/assign-operator`}>
                  <EditIcon className='icon--small' />
                  <span className='font-dark-grey ml1 h6'>Assign Driver</span>
                </Link>
              </div>
            </div>
          )}
          <div
            className='MenuItem flex items-center cursor--pointer p1'
            onClick={props.removeVehicle(props.filters, id)}
          >
            <Exclude className='icon--small' />
            <span className='font-dark-grey ml1 h6'>Remove</span>
          </div>
          <div className='MenuItem flex items-center cursor--pointer p1'>
            <Link to={`/vehicles/edit/${props.vehicle.id}/details`}>
              <EditIcon className='icon--small' />
              <span className='font-dark-grey ml1 h6'>Edit</span>
            </Link>
          </div>
        </ContextMenu>
      </div>
    )
  }

  const deactiveVehicle = (item: IVehicleFilters, id: string) => () => {
    return props.deactivateVehicleAndFetchAllVehicles(item, id)
  }
  const activeVehicle = (item: IVehicleFilters, id: string) => () => {
    return props.activateVehicleAndFetchAllVehicles(item, id)
  }
  const removeVehicle = (item: IVehicleFilters, id: string) => () => {
    return props.removeVehicleAndFetchAllVehicles(item, id)
  }
  const unassignDriver = (id: string, operatorId: string) => async () => {
    const res = await props.unassignDriver(id, operatorId, {
      ...props.filters,
      page: 0,
      take: pageSize,
      recordsPerPage: pageSize
    })

    console.log('res: ', res)

    if (res.status === 200) {
      notification.addNotification({
        title: res.data.success ? 'Success' : '',
        level: res.data.success ? 'success' : '',
        message: res.data.message
      })
    }
  }

  useEffect(() => {
    fetchData({ pageIndex: 0, pageSize })
  }, [props.list, props.filters])

  useEffect(() => {
    props.updateVehicleFilters({
      take: pageSize,
      recordsPerPage: pageSize,
      orderBy: 'vehicles.checkoutDate',
      sort: 'DESC'
    })
  }, [pageSize])

  useEffect(() => {
    setPageCount(Math.ceil(props.totalCount / pageSize))
  }, [props.totalCount, pageSize])

  const fetchData = useCallback(async ({ pageSize, pageIndex }) => {
    setLoading(true)

    await props.fetchAllVehicles({
      ...props.filters,
      page: pageIndex,
      take: pageSize,
      type: props.list,
      recordsPerPage: pageSize
    })
    setPageSize(pageSize)
    setLoading(false)
    // setPageCount(Math.ceil(data.total / pageSize))
  }, [props.vehicles])

  //Declaration of URL's
  const getLink = (): string => {
    let link = ''
    if (process.env.REACT_APP_GATEWAY_URL) {
      link = process.env.REACT_APP_GATEWAY_URL
      if (process.env.REACT_APP_GATEWAY_URL.includes('dev')) {
        const lnk = process.env.REACT_APP_GATEWAY_URL.split(':')
        link = lnk[1]
      } else if (process.env.REACT_APP_GATEWAY_URL.includes('radar')) {
        const lnk1 = process.env.REACT_APP_GATEWAY_URL.split(':')
        link = lnk1[1]
      }
    }
    return link
  }
  //Declaration of URL's
  return (
    <React.Fragment>
      <div className='react-table'>
        <Table
          columns={columns}
          data={props.vehicles || []}
          fetchData={fetchData}
          loading={loading}
          filters={props.filters}
          pageCount={pageCount}
          updateFilters={props.updateVehicleFilters}
          pageSize={pageSize}
          setPageSize={setPageSize}
          pageIndex={pageIndex}
          setPageIndex={setPageIndex}
        />
      </div>
      {/* Footer */}
      <div
        className='footer-copyright border-box text-left py-3 font-black h6 bold pl2'
        style={{ marginBottom: '5rem', marginTop: '2rem' }}
      >
        &copy; Copyright {new Date().getFullYear()}{' '}
        <a href={`${getLink()}`}>
          <u>GOIN'</u>
        </a>{' '}
        All rights reserved.
      </div>
      {/* Footer */}
    </React.Fragment>
  )
}



// WEBPACK FOOTER //
// ./src/components/vehicles/VehiclesTable.tsx